import { AccountCreateModal } from '@accounts'
import { CurrencyIcon } from '@components'
import { MODAL_SIZE } from '@constants'
import { ChevronDownBlackIcon, ChevronUpBlackIcon } from '@icons'
import cn from 'classnames'
import { isOwnerOrAdministrator, selectCurrentUserRole, useAppSelector } from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { formatMoney, GlobalContext, isValidArray, isValidString, tr, useDropDownMenu } from 'mmfintech-commons'
import { Button } from 'mmfintech-portal-commons'
import type React from 'react'
import { isValidElement, type ReactNode, useContext, useEffect, useState } from 'react'
import './SelectAccount.scss'

interface Props {
  staticDisplay?: boolean
  accounts?: AccountBalanceResponse[]
  error?: string
  label?: ReactNode
  showAddButton?: boolean
  showAllAccounts?: boolean
  selectedAccount?: AccountBalanceResponse
  setSelectedAccount?: (value: AccountBalanceResponse) => void
  filteredCurrencies?: string[]
  noShadow?: boolean
  hasExchangeAssets?: boolean
  className?: string
  showId?: boolean
  noBorder?: boolean
  labelStyles?: React.CSSProperties
  extraSection?: ReactNode
  onAddAccount?: (currency: string, accountId: number) => void
}

function SelectAccount({
  staticDisplay = false, // if set, static account display will be presented
  accounts, // list of accounts to select from
  error, // error message (if needed)
  label, // label above the selection box
  showAddButton = false, // if set an "Add New Account" button will be added at the end of the list
  showAllAccounts = false, // if set, "All Account" option will be added at the beginning of the list
  selectedAccount, // selected account
  setSelectedAccount, // selected account setter
  filteredCurrencies, // list of supported currencies for the add new account screen
  noShadow = false,
  hasExchangeAssets = true,
  className,
  showId = false, // shows account id next to name
  noBorder = false, // hides border
  labelStyles,
  extraSection = null, // react node
  onAddAccount
}: Props) {
  const customerRole = useAppSelector(selectCurrentUserRole)

  const [filteredAccounts, setFilteredAccounts] = useState<AccountBalanceResponse[]>([])
  const [preselectAccountId, setPreselectAccountId] = useState(0)
  const { modalShow } = useContext(GlobalContext)

  const { visible, hideDropDown, toggleDropDown } = useDropDownMenu()

  function toggle() {
    return !staticDisplay && toggleDropDown()
  }

  function handleSelect(account: AccountBalanceResponse): void {
    hideDropDown()
    setSelectedAccount(account)
  }

  function handleCreateSuccess(currency: string, accountId: AccountBalanceResponse['id']) {
    if (typeof onAddAccount === 'function') {
      onAddAccount(currency, Number(accountId))
    } else {
      setPreselectAccountId(Number(accountId))
    }
  }

  function handleNewAccountClick() {
    modalShow({
      options: { closeOnClickOutside: false, size: MODAL_SIZE.AUTO, overflow: 'none' },
      content: <AccountCreateModal filteredCurrencies={filteredCurrencies} onSuccess={handleCreateSuccess} />
    })
  }

  const {
    balance,
    currencyCode,
    name,
    id,
    dedicatedMerchantBankAccounts: selectedAccountDedicatedMerchantBankAccounts
  } = selectedAccount || {}
  const { iban: selectedAccountIban } = selectedAccountDedicatedMerchantBankAccounts?.[0] || {}
  useEffect(() => {
    const list = Array.isArray(accounts) ? accounts : []
    setFilteredAccounts(list)

    if (preselectAccountId) {
      const find = list.find(account => account.id === preselectAccountId)
      setPreselectAccountId(0)
      handleSelect(find)
    }
    // eslint-disable-next-line
  }, [accounts])

  return (
    <div className={cn('select-account-wrapper', className)} data-test='account-dropdown'>
      {label && (
        <div className='select-account-label' style={labelStyles}>
          {label}
        </div>
      )}
      <div className='select-account-styled'>
        <div
          className={cn('account-button-styled', {
            'no-shadow': noShadow,
            static: staticDisplay,
            error: error?.length,
            'no-border': noBorder
          })}
          onClick={toggle}
        >
          {selectedAccount ? (
            <span className='account-info'>
              <span className='icon'>
                <CurrencyIcon currency={currencyCode} size='26px' />
              </span>
              <span className='content'>
                {id ? (
                  <span className='balance'>
                    <b>{tr('PORTAL.COMPONENTS.SELECT_ACCOUNT.BALANCE', 'Balance')}:</b>{' '}
                    {formatMoney(balance, currencyCode)}
                  </span>
                ) : null}
                {name && (
                  <span>
                    {name} {showId && id ? ` - ID: ${id}` : ''}
                  </span>
                )}
                {selectedAccountIban && <span>IBAN: {selectedAccountIban}</span>}
              </span>
            </span>
          ) : (
            <span className='no-account'>
              {showAllAccounts
                ? tr('PORTAL.COMPONENTS.SELECT_ACCOUNT.ALL_ACCOUNTS', 'All accounts')
                : tr('PORTAL.COMPONENTS.SELECT_ACCOUNT.PLACEHOLDER', 'Select account')}
            </span>
          )}
          {!staticDisplay && (
            <span className='chevron'>{visible ? <ChevronUpBlackIcon /> : <ChevronDownBlackIcon />}</span>
          )}
        </div>

        {!staticDisplay && (
          <div className={cn('account-list-styled', { opened: visible })} data-test='accounts-menu-content'>
            {showAllAccounts && (
              <div className='account-styled' data-test='all-accounts-menu-item' onClick={() => handleSelect(null)}>
                <span className='icon' />
                <span className='content'>
                  <span className='balance'>{tr('PORTAL.COMPONENTS.SELECT_ACCOUNT.ALL_ACCOUNTS', 'All accounts')}</span>
                </span>
              </div>
            )}
            {!isValidArray(filteredAccounts) && (
              <div className='account-styled disabled' data-test='all-accounts-menu-item'>
                <span className='icon'>
                  <span className='icon-all'>N/A</span>
                </span>
                <span className='content'>
                  <span className='balance'>
                    <b>
                      {hasExchangeAssets
                        ? tr('PORTAL.COMPONENTS.SELECT_ACCOUNT.NO_ACCOUNTS', 'No eligible accounts')
                        : tr(
                            'PORTAL.COMPONENTS.SELECT_ACCOUNT.CANNOT_EXCHANGE',
                            'Currently this asset cannot be exchanged'
                          )}
                    </b>
                  </span>
                </span>
              </div>
            )}
            {filteredAccounts?.map(account => {
              const { id, currencyCode, balance, name: accName, dedicatedMerchantBankAccounts } = account

              const { iban } = dedicatedMerchantBankAccounts?.[0] || {}
              return (
                <div
                  key={id}
                  onClick={() => handleSelect(account)}
                  data-test='account-menu-item'
                  className='account-styled option'
                >
                  <span className='icon'>
                    <CurrencyIcon currency={currencyCode} size='26px' />
                  </span>
                  <span className='content'>
                    <span className='balance'>
                      {tr('PORTAL.COMPONENTS.SELECT_ACCOUNT.BALANCE', 'Balance')}: {formatMoney(balance, currencyCode)}
                    </span>
                    <span>{accName}</span>
                    {iban && <span>IBAN: {iban}</span>}
                  </span>
                </div>
              )
            })}
            {showAddButton && isOwnerOrAdministrator(customerRole) && (
              <div className='add-button-wrapper'>
                <Button
                  type='button'
                  color='round-secondary'
                  text={tr('PORTAL.COMPONENTS.SELECT_ACCOUNT.ADD_BUTTON', 'Add new account')}
                  onClick={handleNewAccountClick}
                />
              </div>
            )}
            {isValidElement(extraSection) ? extraSection : null}
          </div>
        )}
      </div>

      {isValidString(error) && <div className='select-account-error'>{error}</div>}
    </div>
  )
}

export default SelectAccount
